import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, MapPin, Linkedin, Clapperboard } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Seo } from "../components/shared/Seo";
import { SeoHeader } from "../components/shared/SeoHeader";
import { SeoFooter } from "../components/shared/SeoFooter";
import { useReveal } from "../hooks/useReveal";
import { bookingUrl, onBookingClick, SITE_URL, LINKEDIN_URL } from "../lib/booking";
import { getCity } from "../data/cities";
import { EXPERTISES } from "../data/expertises";
import { EXPERTISE_PAGES_ENABLED } from "../lib/flags";

const CityPage = ({ slugProp }) => {
    const params = useParams();
    const slug = slugProp || params.slug;
    const data = getCity(slug);
    useReveal(slug);

    if (!data) return <Navigate to="/" replace />;

    const ctaSource = `ville-${data.slug.replace("personal-branding-", "")}`;
    const canonical = `/${data.slug}`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `Brand Your Expertise — Personal Branding ${data.city}`,
            image: `${SITE_URL}/og-image.jpg`,
            founder: "Fabien Moutier",
            description: data.metaDescription,
            url: `${SITE_URL}${canonical}`,
            areaServed: data.city,
            address: {
                "@type": "PostalAddress",
                addressLocality: data.city,
                addressCountry: "CH",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Accueil",
                    item: SITE_URL,
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: `Personal Branding ${data.city}`,
                    item: `${SITE_URL}${canonical}`,
                },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: data.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ];

    return (
        <div className="App bg-[#070707] min-h-screen" data-testid="city-page">
            <Seo
                title={data.metaTitle}
                description={data.metaDescription}
                canonical={canonical}
                keywords={data.keywords}
                jsonLd={jsonLd}
            />
            <SeoHeader ctaSource={ctaSource} />

            <main>
                {/* Hero */}
                <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden">
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at 75% 0%, rgba(195,157,99,0.12), transparent 55%)",
                        }}
                    />
                    <div className="absolute inset-0 bye-grain pointer-events-none" />
                    <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-7">
                            <Link
                                to="/"
                                className="flex w-fit items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 hover:text-white mb-8 bye-fadeup"
                                data-testid="city-back-home"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                Retour à l'accueil
                            </Link>
                            <div className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5 bye-fadeup">
                                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                                {data.eyebrow}
                            </div>
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.02] bye-fadeup">
                                {data.h1}{" "}
                                <span className="italic bye-gold-text">
                                    {data.h1Accent}
                                </span>
                            </h1>
                            <p className="mt-7 text-base md:text-lg text-white/65 leading-relaxed bye-fadeup">
                                {data.intro}
                            </p>
                            <div className="mt-9 flex flex-wrap gap-4 bye-fadeup">
                                <a
                                    href={bookingUrl(ctaSource)}
                                    onClick={onBookingClick(ctaSource)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                                    data-testid="city-hero-cta"
                                >
                                    Planifier un rendez-vous
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5 bye-fadeup">
                            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                                <img
                                    src={data.studioImg}
                                    alt={`Studio ${data.city} — tournage personal branding et vidéos courtes`}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                                    <div>
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#c39d63] mb-1">
                                            {data.region}
                                        </div>
                                        <div className="font-serif text-3xl text-white">
                                            Studio {data.city}
                                        </div>
                                    </div>
                                    <MapPin className="w-5 h-5 text-[#c39d63]" strokeWidth={1.5} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Bullets */}
                <section className="relative py-8 md:py-12">
                    <div className="relative max-w-6xl mx-auto px-6 md:px-12">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
                            {data.bullets.map((b, i) => (
                                <div
                                    key={i}
                                    className="bye-card p-7 md:p-8 bye-fadeup"
                                    style={{ transitionDelay: `${i * 80}ms` }}
                                    data-testid={`city-bullet-${i}`}
                                >
                                    <div className="font-serif text-2xl text-[#c39d63] mb-3">
                                        0{i + 1}
                                    </div>
                                    <p className="text-sm md:text-base text-white/70 leading-relaxed">
                                        {b}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Présentation Fabien */}
                <section className="relative py-16 md:py-24 bg-[#070707]">
                    <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-5 bye-fadeup">
                            <div className="relative">
                                <div
                                    className="absolute -inset-4 rounded-2xl border border-[#c39d63]/30"
                                    aria-hidden="true"
                                />
                                <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#c39d63]/10 blur-2xl" />
                                <img
                                    src="/images/fabien-portrait.jpg"
                                    alt={`Fabien Moutier — personal branding et vidéos courtes à ${data.city}`}
                                    className="relative w-full rounded-2xl border border-white/10 object-cover aspect-[4/5]"
                                    data-testid="city-about-photo"
                                />
                                <div className="absolute -bottom-5 left-6 right-6 mx-auto flex items-center justify-between bg-[#0a0a0a]/90 backdrop-blur border border-white/10 rounded-full px-5 py-3">
                                    <div>
                                        <div className="text-white font-medium leading-none">
                                            Fabien Moutier
                                        </div>
                                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#c39d63] mt-1">
                                            Fondateur
                                        </div>
                                    </div>
                                    <a
                                        href={LINKEDIN_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex w-9 h-9 rounded-full bg-[#c39d63] items-center justify-center text-[#0a0a0a] hover:bg-[#e6c891] transition-colors"
                                        data-testid="city-about-linkedin"
                                        aria-label="LinkedIn de Fabien Moutier"
                                    >
                                        <Linkedin className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7 bye-fadeup">
                            <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                                Qui vous accompagne
                            </div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-7 leading-tight">
                                Un seul interlocuteur,{" "}
                                <span className="italic text-white/70">
                                    de A à Z.
                                </span>
                            </h2>
                            <div className="space-y-5 text-white/70 leading-relaxed text-base md:text-lg max-w-xl">
                                <p>
                                    Je m'appelle Fabien Moutier. J'écris, je
                                    dirige et je publie les prises de parole de
                                    dirigeants exigeants — CEO, fondateurs, top
                                    management. Plus de 60 experts accompagnés,
                                    plus de 1 000 scripts rédigés.
                                </p>
                                <p>
                                    À {data.city}, vous ne travaillez pas avec
                                    une agence anonyme : c'est moi qui cadre
                                    votre stratégie, écris vos sujets et vous
                                    dirige sur le plateau. Une seule voix, zéro
                                    relais.
                                </p>
                            </div>

                            <div className="mt-8 flex flex-wrap gap-8">
                                <div>
                                    <div className="font-serif text-3xl text-[#c39d63]">
                                        60+
                                    </div>
                                    <div className="text-xs text-white/50 mt-1">
                                        experts accompagnés
                                    </div>
                                </div>
                                <div>
                                    <div className="font-serif text-3xl text-[#c39d63]">
                                        1 000+
                                    </div>
                                    <div className="text-xs text-white/50 mt-1">
                                        scripts rédigés
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex items-start gap-3 rounded-xl border border-[#c39d63]/30 bg-[#c39d63]/5 p-4 text-sm text-white/70 max-w-xl">
                                <Clapperboard
                                    className="w-4 h-4 text-[#c39d63] shrink-0 mt-0.5"
                                    strokeWidth={1.5}
                                />
                                <span>
                                    Les vidéos tournées à {data.city} arrivent
                                    très bientôt. En attendant, découvrez mes
                                    réalisations dans le portfolio.
                                </span>
                            </div>

                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link
                                    to="/#portfolio"
                                    className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                                    data-testid="city-portfolio-link"
                                >
                                    Voir le portfolio
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </Link>
                                <a
                                    href={bookingUrl(`${ctaSource}-about`)}
                                    onClick={onBookingClick(`${ctaSource}-about`)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-sm text-white/80 hover:text-white hover:border-[#c39d63]/50 transition-colors"
                                    data-testid="city-about-cta"
                                >
                                    Planifier un rendez-vous
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Expertises links */}
                {EXPERTISE_PAGES_ENABLED && (
                <section className="relative py-14 md:py-20 border-y border-white/10">
                    <div className="relative max-w-6xl mx-auto px-6 md:px-12">
                        <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-6 bye-fadeup">
                            Expertises accompagnées à {data.city}
                        </div>
                        <div className="flex flex-wrap gap-3 bye-fadeup">
                            {EXPERTISES.map((e) => (
                                <Link
                                    key={e.slug}
                                    to={`/expertise/${e.slug}`}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-sm text-white/70 hover:text-white hover:border-[#c39d63]/50 transition-colors"
                                    data-testid={`city-expertise-${e.slug}`}
                                >
                                    {e.navLabel}
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
                )}

                {/* CTA band */}
                <section className="relative py-20 md:py-28 overflow-hidden">
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at 50% 50%, rgba(195,157,99,0.10), transparent 60%)",
                        }}
                    />
                    <div className="absolute inset-0 bye-grain pointer-events-none" />
                    <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center bye-fadeup">
                        <div className="text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                            Brief en 20 minutes
                        </div>
                        <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
                            Lançons votre personal branding à{" "}
                            <span className="italic text-white/70">
                                {data.city}.
                            </span>
                        </h3>
                        <a
                            href={bookingUrl(`${ctaSource}-band`)}
                            onClick={onBookingClick(`${ctaSource}-band`)}
                            target="_blank"
                            rel="noreferrer"
                            className="group mt-9 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                            data-testid="city-band-cta"
                        >
                            Planifier un rendez-vous
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>
                </section>

                {/* FAQ */}
                <section className="relative py-16 md:py-24 border-t border-white/10">
                    <div className="relative max-w-3xl mx-auto px-6 md:px-12">
                        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-10 bye-fadeup">
                            Questions{" "}
                            <span className="italic text-white/70">
                                fréquentes
                            </span>
                        </h2>
                        <Accordion
                            type="single"
                            collapsible
                            className="bye-fadeup"
                            data-testid="city-faq"
                        >
                            {data.faq.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    className="border-white/10"
                                >
                                    <AccordionTrigger className="text-left text-white hover:text-[#c39d63] font-serif text-lg">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-white/60 leading-relaxed">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </section>
            </main>

            <SeoFooter />
        </div>
    );
};

export default CityPage;
